/**
 * Canonical pitch-deck starting payload for "new from template".
 *
 * Mirrors the 8 slides published to the slide library under
 * ``app/services/deck_io/slide_library/`` and deployed to the
 * ``thread-canonical-pitch`` slug on the API side. Keeping this as a
 * frontend constant means the new-deck flow can pre-fill on the client
 * without a synchronous backend round-trip — the user lands on the
 * editor with the canonical slides already populated.
 *
 * The bodyHtml fields are intentionally lifted verbatim from the slide
 * library JSON so the renderer round-trips identical output.
 */

import type { DeckJson } from "./types";

export const CANONICAL_THEME = "thread-v5-3d";

export function buildCanonicalPitchDeckJson(title: string): DeckJson {
  return {
    schemaVersion: 1,
    docTitle: title,
    cacheTag: `${CANONICAL_THEME}-canonical`,
    provenance: {},
    slides: CANONICAL_SLIDES.map((s) => ({ ...s })),
  };
}

const CANONICAL_SLIDES: DeckJson["slides"] = [
  {
    section: "I · OPENER",
    slug: "opener-thread-thesis",
    titleHtml: "Thread &mdash; the provenance backbone for simulation data.",
    meta: false,
    bodyHtml:
      '    <div class="logo-bar">\n' +
      '        <img src="flexcompute-logo.svg" class="fc-logo" alt="Flexcompute">\n' +
      '        <img src="../_common/assets/thread-logo-lockup-white.svg" class="thread-logo" alt="Thread">\n' +
      "    </div>\n" +
      '    <div style="margin-top: 0.6em;">\n' +
      '        <h2 style="font-size: 0.9em; font-style: normal; color: var(--fc-gray); margin-bottom: 0.6em;">\n' +
      "            Thread &mdash; Provenance, Flat Views &amp; Adaptive ADB\n" +
      "        </h2>\n" +
      "    </div>",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
  {
    section: "II · DBM",
    slug: "data-black-market",
    titleHtml: "The Data Black Market",
    meta: false,
    bodyHtml:
      "    <h1>The Data Black Market</h1>\n" +
      "    <h2>A cert engineer asks one question. Watch what happens on most teams.</h2>",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
  {
    section: "II · DBM",
    slug: "now-with-thread",
    titleHtml: "Now With Thread",
    meta: false,
    bodyHtml:
      "    <h1>Now With Thread</h1>\n" +
      "    <h2>Same team. Same scripts. Provenance is automatic.</h2>",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
  {
    section: "III · MODEL",
    slug: "selection-as-action",
    titleHtml: "The flat view can&rsquo;t invent provenance.",
    meta: false,
    bodyHtml:
      "    <h1>Selection is the action.</h1>\n" +
      "    <h2>Pick a cell in the flat view, walk back to its lineage.</h2>",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
  {
    section: "III · MODEL",
    slug: "provenance-dag-branches",
    titleHtml: "Trust But Verify",
    meta: false,
    bodyHtml:
      "    <h1>Every result chained to geometry, mesh, solver config.</h1>",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
  {
    section: "IV · PAYOFF",
    slug: "what-thread-unlocks",
    titleHtml: "What Thread Unlocks",
    meta: false,
    bodyHtml:
      '    <h1 style="font-size: 1.1em; margin-bottom: 0.4em;">What Thread Unlocks</h1>\n' +
      "    <h2>Four capabilities. One managed database.</h2>",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
  {
    section: "IV · PAYOFF",
    slug: "not-solving",
    titleHtml: "What This Means in Production",
    meta: false,
    bodyHtml:
      "    <h1>What this means in production.</h1>\n" +
      "    <h2>Two things in one tool — auditable ADB views plus the next one cheaper.</h2>",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
  {
    section: "V · CLOSE",
    slug: "close-thread-thesis",
    titleHtml: "",
    meta: false,
    bodyHtml: "    <!-- Cinematic close: T-mark animation, thesis reprise. -->",
    renderSignature: "(root, titleHtml)",
    renderPrefix: "\n            ",
  },
];
